import * as React from "react";

import { type VariantProps, cva } from "cva";

import { cn } from "@ndpco/ui/lib/cn";

const badgeVariants = cva({
  base: "inline-flex items-center gap-x-1.5 rounded-md font-semibold forced-colors:outline",

  variants: {
    size: {
      sm: "px-1.5 py-0.5 text-xs/5",
      md: "px-1.5 py-0.5 text-xs/5",
      lg: "px-3 py-2 text-sm/5",
    },
    style: {
      default: "bg-[var(--badge-bg)] text-[var(--badge-text)]",
      pill: "rounded-full",
      inverse: "bg-transparent text-[var(--badge-text)]",
    },
    variant: {
      default:
        "[--badge-bg:var(--color-zinc-600)]/10 [--badge-text:var(--color-zinc-700)] dark:[--badge-bg:theme(colors.white.5%)] dark:[--badge-text:var(--color-zinc-400)]",
      destructive:
        "[--badge-bg:var(--color-red-500)]/15 [--badge-text:var(--color-red-700)] dark:[--badge-bg:var(--color-red-500)]/10 dark:[--badge-text:var(--color-red-400)]",
      success:
        "[--badge-bg:var(--color-emerald-500)]/15 [--badge-text:var(--color-emerald-700)] dark:[--badge-bg:var(--color-emerald-500)]/10 dark:[--badge-text:var(--color-emerald-400)]",
      warning:
        "[--badge-bg:var(--color-amber-400)]/20 [--badge-text:var(--color-amber-700)] dark:[--badge-bg:var(--color-amber-400)]/10 dark:[--badge-text:var(--color-amber-400)]",
      info: "[--badge-bg:var(--color-blue-500)]/15 [--badge-text:var(--color-blue-700)] dark:[--badge-text:var(--color-blue-400)]",
    },
  },
  defaultVariants: {
    variant: "default",
    style: "default",
    size: "sm",
  },
});

type BadgeProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof badgeVariants>;

function Badge({ className, variant, size, style = "default", ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant, size, style }), className)} {...props} />;
}

export { Badge, badgeVariants, type BadgeProps };
